import { useNavigate } from "react-router";
import { tenantApi, CreateTenantDto } from "@/api/tenant.api";
import { useTenantStore } from "@/store/tenant/tenant.store";
import { Button } from "@/components/ui/button";
import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import { toast } from "sonner";
import { ChevronsUpDown, Loader2, LogOut } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useUserData } from "@/api/user.api";
import { useLogout } from "@/api/auth.api";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { FormField } from "@/components/ui/form-field";
import { t } from "i18next";
import { Routes } from "@/router/routes";

const createTenantSchema = z.object({
    name: z.string().min(1, { message: t("tenant.organization.name.required") }),
    billing: z.object({
        address: z.string().min(1, { message: t("tenant.billing.street.required") }),
        city: z.string().min(1, { message: t("tenant.billing.city.required") }),
        country: z.string().min(1, { message: t("tenant.billing.country.required") }),
        postalCode: z.string().min(1, { message: t("tenant.billing.zip.required") }),
        email: z.string().email({ message: t("tenant.billing.email.invalid") }),
    }),
});

type CreateTenantFormData = z.infer<typeof createTenantSchema>;

/**
 * Create a new tenant
 */
export const CreateTenant = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { selectTenant } = useTenantStore();
    const { data: userData } = useUserData();
    const { mutate: logout } = useLogout();

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting, touchedFields },
    } = useForm<CreateTenantFormData>({
        resolver: zodResolver(createTenantSchema),
        defaultValues: {
            name: "",
            billing: {
                address: "",
                city: "",
                country: "",
                postalCode: "",
                email: "",
            },
        },
        mode: "onChange",
    });

    const onSubmit = async (data: CreateTenantFormData) => {
        try {
            // Create the tenant
            const tenant = await tenantApi.createTenant(data as CreateTenantDto);

            // Select the new tenant
            selectTenant(tenant);

            toast.success("Organization created successfully");

            // Navigate to the home page
            navigate(`/${Routes.Tenant}/${tenant.id}`);
        } catch (error) {
            console.error("Failed to create tenant:", error);
            toast.error("Failed to create organization. Please try again.");
        }
    };

    return (
        <>
            <title>{`Exibra CMS - ${t("tenant.create.title")}`}</title>
            <div className="flex justify-center items-center min-h-screen p-4 sm:p-6">
                <Card className="w-full max-w-[60rem] p-4 sm:p-6">
                    <CardHeader className="flex flex-col sm:flex-row justify-between gap-6">
                        <div className="max-w-[25rem] flex flex-col gap-2">
                            <CardTitle>{t("tenant.create.title")}</CardTitle>
                            <CardDescription>{t("tenant.create.description")}</CardDescription>
                        </div>

                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    variant="ghost"
                                    className="flex items-center gap-4 w-fit py-6"
                                >
                                    <Avatar className="h-8 w-8 rounded-lg">
                                        <AvatarImage src={undefined} alt={userData?.data?.name} />
                                        <AvatarFallback className="rounded-lg">
                                            {userData?.data?.name?.charAt(0)}
                                        </AvatarFallback>
                                    </Avatar>
                                    <div className="grid flex-1 text-left text-sm leading-tight">
                                        <span className="font-semibold text-ellipsis md:text-nowrap">
                                            {userData?.data?.name}
                                        </span>
                                        <span className="text-xs text-ellipsis md:text-nowrap">
                                            {userData?.data?.email}
                                        </span>
                                    </div>
                                    <ChevronsUpDown className="ml-auto size-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                                className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
                                side="bottom"
                                align="end"
                                sideOffset={4}
                            >
                                <DropdownMenuItem
                                    onClick={() => logout()}
                                    className="cursor-pointer"
                                >
                                    <LogOut />
                                    <p className="text-sm">{t("general.logout")}</p>
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6">
                            <div className="space-y-6">
                                <FormField
                                    id="name"
                                    label={t("tenant.organization.name")}
                                    placeholder={t("general.inputPlaceholder")}
                                    registration={register("name")}
                                    error={errors.name?.message}
                                    touched={touchedFields.name}
                                    required
                                    data-1p-ignore
                                />

                                <div className="pt-4 border-t">
                                    <h3 className="font-medium mb-4">
                                        {t("tenant.billing.address")}
                                    </h3>

                                    <div className="space-y-4">
                                        <FormField
                                            id="billing.address"
                                            label={t("tenant.billing.street")}
                                            placeholder={t("general.inputPlaceholder")}
                                            registration={register("billing.address")}
                                            error={errors.billing?.address?.message}
                                            touched={touchedFields.billing?.address}
                                            required
                                        />

                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <FormField
                                                id="billing.city"
                                                label={t("tenant.billing.city")}
                                                placeholder={t("general.inputPlaceholder")}
                                                registration={register("billing.city")}
                                                error={errors.billing?.city?.message}
                                                touched={touchedFields.billing?.city}
                                                required
                                            />
                                            <FormField
                                                id="billing.postalCode"
                                                label={t("tenant.billing.zip")}
                                                placeholder={t("general.inputPlaceholder")}
                                                registration={register("billing.postalCode")}
                                                error={errors.billing?.postalCode?.message}
                                                touched={touchedFields.billing?.postalCode}
                                                required
                                            />
                                        </div>

                                        <FormField
                                            id="billing.country"
                                            label={t("tenant.billing.country")}
                                            placeholder={t("general.inputPlaceholder")}
                                            registration={register("billing.country")}
                                            error={errors.billing?.country?.message}
                                            touched={touchedFields.billing?.country}
                                            required
                                        />

                                        <FormField
                                            id="billing.email"
                                            type="email"
                                            label={t("tenant.billing.email")}
                                            placeholder="billing@example.com"
                                            registration={register("billing.email")}
                                            error={errors.billing?.email?.message}
                                            touched={touchedFields.billing?.email}
                                            required
                                        />
                                    </div>
                                </div>
                            </div>

                            <CardFooter className="flex justify-end p-0">
                                <Button type="submit" disabled={isSubmitting} className="w-[10rem]">
                                    {isSubmitting ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            {t("general.creating")}
                                        </>
                                    ) : (
                                        t("general.create")
                                    )}
                                </Button>
                            </CardFooter>
                        </form>
                    </CardContent>
                </Card>
            </div>
        </>
    );
};
